import axios from 'axios'
import apiClient from '@/services/http/client'
import type {
  AdminMetrics, AdminStudio, AdminSetupRequest,
  CompleteAndConnectPayload, CompleteAndConnectResponse,
  AdminStudioDetail, AdminStudioStatus,
  AdminAlert, AdminStudioActivityItem, AdminImpersonationSession,
  AdminStudioBillingData, AdminStudioMetricsHistoryData, AdminStudioUsageData, AdminAiUsageData,
  AdminDashboardData,
  AdminSubscriptionMutableStatus,
  AdminStudioSubscriptionMutableData,
  AdminBroadcast,
  AdminBroadcastPayload,
  AdminStudioOnboardingData,
  AdminSystemHealthData,
  AdminFailedJob,
  AdminIntegrationError,
  AdminUserSummary,
  AdminUserSession,
} from '@/types'
import { API_BASE_URL } from '@/config/env'
import { STORAGE_KEYS } from '@/constants/storage'

export const fetchAdminMetrics = async (): Promise<AdminMetrics> => {
  const response = await apiClient.get<{ data: AdminMetrics }>('/api/v1/admin/metrics')
  return response.data.data
}

export const fetchAdminDashboard = async (): Promise<AdminDashboardData> => {
  const response = await apiClient.get('/api/v1/admin/dashboard')
  return unwrapData<AdminDashboardData>(response.data)
}

export const fetchAdminStudios = async (page = 1): Promise<{ data: AdminStudio[]; meta: { total: number; current_page: number; per_page: number } }> => {
  const response = await apiClient.get('/api/v1/admin/studios', { params: { page, per_page: 20 } })
  return response.data
}

export const fetchAdminStudiosWithFilters = async (params?: {
  page?: number
  per_page?: number
  filter?: string
}): Promise<{ data: AdminStudio[]; meta: { total: number; current_page: number; per_page: number } }> => {
  const response = await apiClient.get('/api/v1/admin/studios', {
    params: {
      page: 1,
      per_page: 20,
      ...params,
    },
  })
  return response.data
}

const unwrapData = <T>(payload: unknown): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

export const fetchAdminStudioDetail = async (id: number): Promise<AdminStudioDetail> => {
  const response = await apiClient.get(`/api/v1/admin/studios/${id}`)
  return unwrapData<AdminStudioDetail>(response.data)
}

export const extendAdminStudioTrial = async (
  id: number,
  payload: { days: number; from_now?: boolean },
): Promise<void> => {
  await apiClient.post(`/api/v1/admin/studios/${id}/extend-trial`, payload)
}

export const updateAdminStudioStatus = async (
  id: number,
  payload: { status: AdminStudioStatus },
): Promise<void> => {
  await apiClient.patch(`/api/v1/admin/studios/${id}/status`, payload)
}

export const updateAdminStudioNotes = async (
  id: number,
  payload: { admin_notes: string | null },
): Promise<void> => {
  await apiClient.patch(`/api/v1/admin/studios/${id}/notes`, payload)
}

export const fetchAdminStudioActivity = async (
  id: number,
  params?: { page?: number; per_page?: number },
): Promise<{
  data: AdminStudioActivityItem[]
  meta?: {
    total?: number
    per_page?: number
    current_page?: number
    last_page?: number
  }
}> => {
  const response = await apiClient.get(`/api/v1/admin/studios/${id}/activity`, {
    params: {
      per_page: 20,
      ...params,
    },
  })

  if (response.data && typeof response.data === 'object' && 'data' in response.data) {
    return response.data as {
      data: AdminStudioActivityItem[]
      meta?: {
        total?: number
        per_page?: number
        current_page?: number
        last_page?: number
      }
    }
  }

  return { data: response.data as AdminStudioActivityItem[] }
}

export const impersonateAdminStudio = async (id: number): Promise<AdminImpersonationSession> => {
  const response = await apiClient.post<{ data: AdminImpersonationSession }>(
    `/api/v1/admin/studios/${id}/impersonate`,
  )
  return response.data.data
}

export const stopAdminImpersonation = async (impersonationToken: string): Promise<void> => {
  await axios.post(
    '/api/v1/admin/impersonate/stop',
    {},
    {
      baseURL: API_BASE_URL,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${impersonationToken}`,
      },
    },
  )
}

export const fetchAdminAlertsUnreadCount = async (): Promise<number> => {
  const response = await apiClient.get<{ data: { count: number } }>('/api/v1/admin/alerts/unread-count')
  return response.data.data.count ?? 0
}

export const fetchAdminAlerts = async (params?: {
  unresolved?: 0 | 1
  page?: number
  per_page?: number
}): Promise<{
  data: AdminAlert[]
  meta?: {
    total?: number
    per_page?: number
    current_page?: number
    last_page?: number
  }
}> => {
  const response = await apiClient.get('/api/v1/admin/alerts', {
    params: {
      unresolved: 1,
      per_page: 50,
      ...params,
    },
  })

  if (response.data && typeof response.data === 'object' && 'data' in response.data) {
    return response.data as {
      data: AdminAlert[]
      meta?: {
        total?: number
        per_page?: number
        current_page?: number
        last_page?: number
      }
    }
  }

  return { data: response.data as AdminAlert[] }
}

export const markAdminAlertAsRead = async (id: number): Promise<void> => {
  await apiClient.patch(`/api/v1/admin/alerts/${id}/read`)
}

export const resolveAdminAlert = async (id: number): Promise<void> => {
  await apiClient.patch(`/api/v1/admin/alerts/${id}/resolved`)
}

export const fetchAdminStudioBilling = async (id: number): Promise<AdminStudioBillingData> => {
  const response = await apiClient.get(`/api/v1/admin/studios/${id}/billing`)
  return unwrapData<AdminStudioBillingData>(response.data)
}

export const cancelAdminStudioSubscription = async (id: number): Promise<void> => {
  await apiClient.post(`/api/v1/admin/studios/${id}/subscription/cancel`)
}

export const fetchAdminStudioMetricsHistory = async (
  id: number,
  months: number,
): Promise<AdminStudioMetricsHistoryData> => {
  const response = await apiClient.get(`/api/v1/admin/studios/${id}/metrics/history`, {
    params: { months },
  })
  return unwrapData<AdminStudioMetricsHistoryData>(response.data)
}

export const fetchAdminStudioUsage = async (id: number): Promise<AdminStudioUsageData> => {
  const response = await apiClient.get(`/api/v1/admin/studios/${id}/usage`)
  return unwrapData<AdminStudioUsageData>(response.data)
}

export const fetchAdminStudioOnboarding = async (id: number): Promise<AdminStudioOnboardingData> => {
  const response = await apiClient.get(`/api/v1/admin/studios/${id}/onboarding`)
  return unwrapData<AdminStudioOnboardingData>(response.data)
}

export const updateAdminStudioSubscription = async (
  id: number,
  payload: {
    plan?: string
    status?: AdminSubscriptionMutableStatus
    subscription_price_id?: number | null
    conversation_limit_override?: number | null
    current_period_start?: string
    current_period_end?: string
    cancel_at_period_end?: boolean
    canceled_at?: string | null
  },
): Promise<AdminStudioSubscriptionMutableData> => {
  const response = await apiClient.patch(`/api/v1/admin/studios/${id}/subscription`, payload)
  return unwrapData<AdminStudioSubscriptionMutableData>(response.data)
}

export const renewAdminStudioSubscriptionPeriod = async (
  id: number,
): Promise<AdminStudioSubscriptionMutableData> => {
  const response = await apiClient.post(`/api/v1/admin/studios/${id}/subscription/renew-period`)
  return unwrapData<AdminStudioSubscriptionMutableData>(response.data)
}

export const fetchAdminBroadcasts = async (params?: {
  page?: number
  per_page?: number
}): Promise<{
  data: AdminBroadcast[]
  meta: {
    current_page: number
    last_page: number
    total: number
  }
}> => {
  const response = await apiClient.get('/api/v1/admin/broadcasts', {
    params: {
      per_page: 20,
      ...params,
    },
  })
  return response.data
}

export const createAdminBroadcast = async (
  payload: AdminBroadcastPayload,
): Promise<AdminBroadcast> => {
  const response = await apiClient.post('/api/v1/admin/broadcasts', payload)
  return unwrapData<AdminBroadcast>(response.data)
}

export const updateAdminBroadcast = async (
  id: number,
  payload: AdminBroadcastPayload,
): Promise<AdminBroadcast> => {
  const response = await apiClient.patch(`/api/v1/admin/broadcasts/${id}`, payload)
  return unwrapData<AdminBroadcast>(response.data)
}

export const publishAdminBroadcast = async (
  id: number,
): Promise<{ id: number; published_at: string }> => {
  const response = await apiClient.post(`/api/v1/admin/broadcasts/${id}/publish`)
  return unwrapData<{ id: number; published_at: string }>(response.data)
}

export const deleteAdminBroadcast = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/v1/admin/broadcasts/${id}`)
}

export const fetchAdminSystemHealth = async (): Promise<AdminSystemHealthData> => {
  const response = await apiClient.get('/api/v1/admin/system/health')
  return unwrapData<AdminSystemHealthData>(response.data)
}

export const fetchAdminFailedJobs = async (params?: {
  page?: number
  per_page?: number
}): Promise<{
  data: AdminFailedJob[]
  meta?: {
    current_page?: number
    last_page?: number
    total?: number
  }
}> => {
  const response = await apiClient.get('/api/v1/admin/system/failed-jobs', {
    params: {
      per_page: 20,
      ...params,
    },
  })
  return response.data
}

export const retryAdminFailedJob = async (uuid: string): Promise<{ uuid: string; status: string }> => {
  const response = await apiClient.post(`/api/v1/admin/system/failed-jobs/${uuid}/retry`)
  return unwrapData<{ uuid: string; status: string }>(response.data)
}

export const forgetAdminFailedJob = async (uuid: string): Promise<void> => {
  await apiClient.delete(`/api/v1/admin/system/failed-jobs/${uuid}`)
}

export const retryAllAdminFailedJobs = async (): Promise<{ retried: number }> => {
  const response = await apiClient.post('/api/v1/admin/system/failed-jobs/retry-all')
  return unwrapData<{ retried: number }>(response.data)
}

export const fetchAdminIntegrationErrors = async (params?: {
  page?: number
  per_page?: number
  provider?: string
  studio_id?: number
}): Promise<{
  data: AdminIntegrationError[]
  meta?: {
    current_page?: number
    last_page?: number
    total?: number
  }
}> => {
  const response = await apiClient.get('/api/v1/admin/system/integration-errors', {
    params: {
      per_page: 30,
      ...params,
    },
  })
  return response.data
}

export const fetchAdminUsers = async (params?: {
  page?: number
  per_page?: number
  q?: string
}): Promise<{
  data: AdminUserSummary[]
  meta?: {
    current_page?: number
    last_page?: number
    total?: number
  }
}> => {
  const response = await apiClient.get('/api/v1/admin/users', {
    params: {
      per_page: 20,
      ...params,
    },
  })
  return response.data
}

export const fetchAdminUserDetail = async (id: number): Promise<AdminUserSummary> => {
  const response = await apiClient.get(`/api/v1/admin/users/${id}`)
  return unwrapData<AdminUserSummary>(response.data)
}

export const fetchAdminUserSessions = async (id: number): Promise<AdminUserSession[]> => {
  const response = await apiClient.get(`/api/v1/admin/users/${id}/sessions`)
  return unwrapData<AdminUserSession[]>(response.data)
}

export const revokeAdminUserSession = async (id: number, tokenId: number): Promise<void> => {
  await apiClient.delete(`/api/v1/admin/users/${id}/sessions/${tokenId}`)
}

export const revokeAllAdminUserSessions = async (id: number): Promise<{ revoked: number }> => {
  const response = await apiClient.delete(`/api/v1/admin/users/${id}/sessions`)
  return unwrapData<{ revoked: number }>(response.data)
}

const buildAdminExportUrl = (endpoint: string, params?: Record<string, string | number | undefined>) => {
  const url = new URL(`${API_BASE_URL}${endpoint}`)
  if (!params) return url.toString()
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') return
    url.searchParams.set(key, String(value))
  })
  return url.toString()
}

const downloadAdminCsv = async (endpoint: string, params?: Record<string, string | number | undefined>) => {
  const token = localStorage.getItem(STORAGE_KEYS.token)
  if (!token) throw new Error('No auth token found')

  const response = await fetch(buildAdminExportUrl(endpoint, params), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'text/csv',
    },
  })

  if (!response.ok) {
    throw new Error('Export failed')
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const contentDisposition = response.headers.get('Content-Disposition') ?? ''
  const fileNameMatch = contentDisposition.match(/filename\\*?=(?:UTF-8''|\"?)([^\";]+)/i)
  const fileName = fileNameMatch?.[1]?.replace(/\"/g, '') || 'export.csv'

  const a = document.createElement('a')
  a.href = url
  a.download = decodeURIComponent(fileName)
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export const exportAdminStudiosCsv = async (params?: { status?: string }) =>
  downloadAdminCsv('/api/v1/admin/exports/studios', params)

export const exportAdminRevenueCsv = async (params: { from: string; to: string; studio_id?: number }) =>
  downloadAdminCsv('/api/v1/admin/exports/revenue', params)

export const exportAdminStudentsCsv = async (params: { studio_id: number }) =>
  downloadAdminCsv('/api/v1/admin/exports/students', params)

export const fetchAdminSetupRequests = async (params?: {
  status?: string
  preferred_provider?: string
  q?: string
  page?: number
}): Promise<{ data: AdminSetupRequest[]; meta: { total: number; current_page: number; per_page: number } }> => {
  const response = await apiClient.get('/api/v1/admin/whatsapp/setup-requests', {
    params: { per_page: 30, ...params },
  })
  return response.data
}

export const updateAdminSetupRequest = async (
  id: number,
  payload: { status?: AdminSetupRequest['status']; internal_notes?: string | null },
): Promise<AdminSetupRequest> => {
  const response = await apiClient.patch<{ data: AdminSetupRequest }>(
    `/api/v1/admin/whatsapp/setup-requests/${id}`,
    payload,
  )
  return response.data.data
}

export const completeAndConnectSetupRequest = async (
  id: number,
  payload: CompleteAndConnectPayload,
): Promise<CompleteAndConnectResponse> => {
  const response = await apiClient.post<CompleteAndConnectResponse>(
    `/api/v1/admin/whatsapp/setup-requests/${id}/complete`,
    payload,
  )
  return response.data
}


export const fetchAdminAiUsage = async (): Promise<AdminAiUsageData> => {
  const response = await apiClient.get<AdminAiUsageData>('/api/v1/admin/ai/usage')
  return response.data
}
